/**
 * IdentityContext — the ONE global "who am I" used across every command-center feature.
 *
 * Joshua's request: select who we are ONCE, and have that identity flow into every
 * feature — task creation/intake (sender/created_by), the Command Center (PersonLane)
 * default lane, chat (sender), content sign-off, file upload (uploaded_by), corrections
 * (corrector), endorse (endorser), bring-in-human (by), conversation creation (creator).
 *
 * Identity is a FREE-FORM name string — ANY human can say who they are, not just the
 * five AI reps. More people use this than the reps we maintain people-pages for. The
 * five reps remain convenient quick-picks (and the only ones with their own command
 * lane), but the switcher is a combobox you can type any name into. Recent names are
 * tracked so the switcher can show recent users.
 *
 * The switcher lives in the Navbar (visible on every page). Persisted to localStorage.
 *
 * Where the FIRST value comes from (changed 2026-08-21): whoever is signed in. Sign-in
 * is by email now (services/auth.ts), and the first time someone logs in they say who
 * they are; that name seeds this identity, so nobody starts out filing work as Joshua
 * because that was the hardcoded default. The switcher still accepts any free-form name
 * afterwards. The identity is stamped with the email that set it, so when a different
 * person signs in on the same browser it resets to THEIR name instead of inheriting the
 * previous user's.
 *
 * Where the LIST of names comes from (changed 2026-09-23): the server, not this file.
 * Any @spiritualdata.org address can create its own account by signing in, and the
 * moment it exists that person is on `/api/auth/people` and therefore in the "You are:"
 * selector, with no code change. The rep slugs below stay as the quick-picks (they are
 * the identities with a command lane, and they are offered even before a rep's first
 * sign-in), and the box is still free-form for anyone else.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { RosterPerson, fetchPeople } from '../services/auth';

// The AI reps — used as quick-picks in the switcher and as the only identities
// that have a dedicated command lane / role config (see PersonLane). Identity itself
// is NOT limited to these.
const PEOPLE = ['joshua', 'subham', 'aazmeer', 'thadeus', 'zee', 'payal', 'travis', 'kaja', 'hassan', 'honelign', 'abrham', 'adeola', 'armin', 'jubaer', 'mary', 'ojassvi', 'prachi', 'joey'] as const;
export type PersonId = (typeof PEOPLE)[number];
export const IDENTITY_PEOPLE: readonly PersonId[] = PEOPLE;

/** True when `name` (case-insensitively) is one of the AI rep slugs. */
export const isRep = (name: string): boolean =>
  !!name && (PEOPLE as readonly string[]).includes(name.trim().toLowerCase());

/** Return the canonical rep slug for a name, or null if it isn't a rep. */
export const repSlug = (name: string): PersonId | null =>
  isRep(name) ? (name.trim().toLowerCase() as PersonId) : null;

// ---- Near-miss detection for the identity combobox -------------------------
// The free-form "You are" box drives lane wiring + attribution. A slightly-off
// rep name (a typo, or "Subham K" vs the "subham" slug) would otherwise commit as
// a NON-rep free-form identity and silently strand the user out of their command
// lane. `nearestRep` finds the closest rep to a typed name so the UI can offer a
// visible "did you mean …" confirm rather than stranding silently. Returns null
// for an exact rep (no near-miss needed) or when nothing is close enough.
const levenshtein = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  let curr = new Array<number>(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
};

/**
 * The closest rep to a typed name, when it's a near-miss (NOT already an exact
 * rep). Matches on: a leading token equal to a rep slug ("Subham K" → subham),
 * a rep slug as a substring, or a small edit distance (typos like "Azmeer").
 * Returns null when the input is already a rep, is empty, or nothing is close —
 * so a genuine non-rep human name is never hijacked into a rep.
 */
export const nearestRep = (raw: string): PersonId | null => {
  const name = (raw || '').trim().toLowerCase();
  if (!name || isRep(name)) return null;
  // First token equals a rep slug, e.g. "subham k" / "zee (ops)".
  const firstToken = name.split(/[\s.()/,-]+/).filter(Boolean)[0];
  if (firstToken && (PEOPLE as readonly string[]).includes(firstToken)) {
    return firstToken as PersonId;
  }
  // Whole input contains a rep slug as a word-ish substring.
  for (const rep of PEOPLE) {
    if (name.includes(rep)) return rep;
  }
  // Edit-distance near-miss: tolerate ~1 typo for short names, ~2 for longer.
  let best: { rep: PersonId; dist: number } | null = null;
  for (const rep of PEOPLE) {
    const dist = levenshtein(name, rep);
    const tolerance = rep.length <= 4 ? 1 : 2;
    if (dist <= tolerance && (!best || dist < best.dist)) best = { rep, dist };
  }
  return best ? best.rep : null;
};

const IDENTITY_KEY = 'cockpit_identity';
const RECENT_KEY = 'cockpit_identity_recent';
// The signed-in email that the stored identity belongs to. Lets a shared browser tell
// "Joshua already picked his name" from "someone else is signed in now".
const IDENTITY_OWNER_KEY = 'cockpit_identity_owner';
const RECENT_CAP = 8;

// Friendly display name for a name/slug. Reps are lowercase slugs we title-case;
// free-form names are shown as typed (just title-case a bare lowercase token).
export const personLabel = (p: string): string =>
  p ? p.charAt(0).toUpperCase() + p.slice(1) : p;

interface IdentityContextValue {
  /** The current global identity (free-form display name; any human). */
  currentPerson: string;
  /** Set the global identity (persisted; also recorded in recentPeople). */
  setCurrentPerson: (who: string) => void;
  /** Recently-used identities, most-recent first (for the switcher). */
  recentPeople: string[];
  /**
   * Everyone on the maintained roster (server-side), reps first. This is what makes a
   * newly created account selectable in the switcher without a code change.
   */
  people: RosterPerson[];
  /** Display names of everyone selectable: the reps, then the rest of the roster. */
  peopleNames: string[];
}

const IdentityContext = createContext<IdentityContextValue | undefined>(undefined);

/**
 * The identity to start from: the stored one when it belongs to the person who is
 * signed in, otherwise the signed-in person's own name.
 */
const readInitial = (sessionUser?: SessionIdentity | null): string => {
  try {
    const saved =
      (typeof localStorage !== 'undefined' && localStorage.getItem(IDENTITY_KEY)) || '';
    const owner =
      (typeof localStorage !== 'undefined' && localStorage.getItem(IDENTITY_OWNER_KEY)) || '';
    const email = (sessionUser?.email || '').toLowerCase();
    const savedBelongsToThisPerson = !email || !owner || owner.toLowerCase() === email;
    if (saved.trim() && savedBelongsToThisPerson) return saved;
  } catch {
    /* ignore */
  }
  if (sessionUser?.name && sessionUser.name.trim()) return sessionUser.name.trim();
  return 'Joshua';
};

const readRecent = (): string[] => {
  try {
    const raw =
      (typeof localStorage !== 'undefined' && localStorage.getItem(RECENT_KEY)) || '';
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    /* ignore */
  }
  return [];
};

// Push a name to the front of the recents list, de-duped case-insensitively, capped.
const pushRecent = (list: string[], who: string): string[] => {
  const name = who.trim();
  if (!name) return list;
  const deduped = list.filter((x) => x.trim().toLowerCase() !== name.toLowerCase());
  return [name, ...deduped].slice(0, RECENT_CAP);
};

/** The signed-in person, as far as identity is concerned. */
export interface SessionIdentity {
  email: string;
  name: string | null;
  rep_slug?: string | null;
}

export const IdentityProvider: React.FC<{
  children: React.ReactNode;
  sessionUser?: SessionIdentity | null;
}> = ({ children, sessionUser }) => {
  const [currentPerson, setCurrentPersonState] = useState<string>(() => readInitial(sessionUser));
  const [recentPeople, setRecentPeople] = useState<string[]>(readRecent);
  const [people, setPeople] = useState<RosterPerson[]>([]);

  // The roster, loaded once per signed-in session. A failure is not worth surfacing:
  // the switcher still has the rep quick-picks and accepts any typed name, so the worst
  // case is the list is shorter, never that identity stops working.
  useEffect(() => {
    let cancelled = false;
    fetchPeople()
      .then((rows) => {
        if (!cancelled) setPeople(rows);
      })
      .catch(() => {
        if (!cancelled) setPeople([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionUser?.email]);

  // A different person signed in on this browser: their name replaces the stored one.
  useEffect(() => {
    if (!sessionUser?.email) return;
    let owner = '';
    try {
      owner = localStorage.getItem(IDENTITY_OWNER_KEY) || '';
    } catch {
      /* ignore */
    }
    if (owner.toLowerCase() === sessionUser.email.toLowerCase()) return;
    const name = (sessionUser.name || '').trim();
    if (!name) return;
    setCurrentPersonState(name);
    try {
      localStorage.setItem(IDENTITY_KEY, name);
      localStorage.setItem(IDENTITY_OWNER_KEY, sessionUser.email.toLowerCase());
    } catch {
      /* ignore */
    }
  }, [sessionUser?.email, sessionUser?.name]);

  const setCurrentPerson = useCallback((whoRaw: string) => {
    const who = (whoRaw || '').trim();
    if (!who) return;
    setCurrentPersonState(who);
    setRecentPeople((prev) => {
      const next = pushRecent(prev, who);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    try {
      localStorage.setItem(IDENTITY_KEY, who);
    } catch {
      /* ignore */
    }
  }, []);

  // Reps first (they carry the command lanes), then everyone else the roster knows,
  // de-duped case-insensitively so a rep who is also a roster row appears once.
  const peopleNames = React.useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    const add = (name: string) => {
      const n = (name || '').trim();
      if (!n || seen.has(n.toLowerCase())) return;
      seen.add(n.toLowerCase());
      out.push(n);
    };
    PEOPLE.forEach((p) => add(personLabel(p)));
    people.forEach((p) => add(p.rep_slug ? personLabel(p.rep_slug) : p.name));
    return out;
  }, [people]);

  return (
    <IdentityContext.Provider
      value={{ currentPerson, setCurrentPerson, recentPeople, people, peopleNames }}
    >
      {children}
    </IdentityContext.Provider>
  );
};

export const useIdentity = (): IdentityContextValue => {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error('useIdentity must be used within an IdentityProvider');
  return ctx;
};
